declare const __GIT_HASH__: string;
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { ArrowLeft, Check } from 'lucide-react';
import { getActiveEnv, setActiveEnv, getAuthApiUrl, type EnvName } from '../config/env';

// Persist the chosen plane as the account's default_env on the identity authority, so the choice
// survives logout and the dashboard lands here on next login. Best-effort — a failed write must not
// block the local switch (the env is applied locally and re-adopted on next login regardless).
async function persistDefaultEnv(name: Exclude<EnvName, 'local'>): Promise<void> {
  const token = localStorage.getItem('auction_access_token');
  if (!token) return;
  const env = name === 'sandbox' ? 'SANDBOX' : 'PRODUCTION';
  try {
    const res = await fetch(`${getAuthApiUrl()}/api/v1/auth/default-env`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ env }),
    });
    if (!res.ok) console.warn(`persistDefaultEnv: authority returned ${res.status}`);
  } catch (err) {
    console.warn('persistDefaultEnv failed (non-fatal):', err);
  }
}

// User-facing test/live toggle. Production = live mode, Sandbox = test mode. The toggle swaps only the
// data plane (auth stays on the constant authority), so it does NOT log the user out — a single account
// spans both planes. We reload the page after switching to refresh all plane-specific state and
// re-initialize the Stripe singleton cleanly.
const ENV_OPTIONS: { name: Exclude<EnvName, 'local'>; label: string; sub: string }[] = [
  { name: 'production', label: 'Live mode', sub: 'Real shows, payments, and orders' },
  { name: 'sandbox', label: 'Test mode', sub: 'Sandbox — safe to experiment' },
];

export function AppInfoPage() {
  useDocumentTitle('App Info');
  const navigate = useNavigate();
  const active = getActiveEnv();

  const selectEnv = async (name: Exclude<EnvName, 'local'>) => {
    if (name === active) return;
    setActiveEnv(name);
    // Persist to default_env before reloading (best-effort) so the choice survives logout. Awaited so
    // the write goes out on the current authority before navigation tears the page down.
    await persistDefaultEnv(name);
    // Reload so every plane-specific fetch and the Stripe singleton pick up the new environment.
    window.location.reload();
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>

          <h1 style={styles.title}>App Info</h1>

          <h2 style={styles.section}>Build</h2>
          <div style={styles.card}>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Version</span>
              <span style={styles.rowValue}>{__GIT_HASH__}</span>
            </div>
          </div>

          <h2 style={styles.section}>Environment</h2>
          <div style={styles.card}>
            {ENV_OPTIONS.map((opt, i) => (
              <button
                key={opt.name}
                onClick={() => selectEnv(opt.name)}
                style={{
                  ...styles.envRow,
                  borderTop: i === 0 ? 'none' : `1px solid ${colors.gray[100]}`,
                }}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={styles.envLabel}>{opt.label}</div>
                  <div style={styles.envSub}>{opt.sub}</div>
                </div>
                {active === opt.name && <Check size={20} color="#5F3DC4" />}
              </button>
            ))}
          </div>
          <p style={styles.note}>Switching modes keeps you logged in — your account works in both.</p>
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px' },
  content: { maxWidth: '600px', margin: '0 auto' },
  backButton: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'none', border: 'none', cursor: 'pointer',
    color: colors.gray[600], fontSize: 14, fontWeight: 500,
    marginBottom: 16, padding: 0,
  },
  title: { fontSize: 28, fontWeight: 700, color: colors.ink.black, marginBottom: 24 },
  section: {
    fontSize: 13, fontWeight: 600, color: colors.gray[600],
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, marginTop: 24,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: 16,
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px',
  },
  rowLabel: { fontSize: 14, fontWeight: 500, color: colors.ink.black },
  rowValue: { fontSize: 14, color: colors.gray[600] },
  envRow: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px', background: 'none', border: 'none', cursor: 'pointer',
  },
  envLabel: { fontSize: 15, fontWeight: 600, color: colors.ink.black },
  envSub: { fontSize: 12, color: colors.gray[600], marginTop: 2 },
  note: { fontSize: 12, color: colors.gray[400], marginTop: 10 },
};
